import { captureException } from "@sentry/react";
import { isSelectableLegacyVoiceModel } from "../../../../src/shared/legacyVoiceRetirement.js";
import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { capitalize } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { default as InlineSVG, default as SVG } from "react-inlinesvg";
import Modal from "react-modal";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { focusVisible } from "../../styles/a11y";
import useMyVoiceSelectedSample from "../../hooks/useMyVoiceSelectedSample";
import useMyVoiceStyleSamples from "../../hooks/useMyVoiceStyleSamples";
import useNovelNovemberSprint from "../../hooks/useNovelNovemberSprint";
import { useProjectStore } from "../../stores/projectStore";
import { useStoryBibleStore } from "../../stores/storyBibleStore";
import { useSudowriteStore } from "../../stores/sudowriteStore";
import MenuSelect from "../../sudokit/components/SudoSelect/MenuSelect.jsx";
import { sendAppEvent } from "../../Utils.js";
import IconButton from "../IconButton";
import Slider from "../Slider";
import MODELS_DRAFT from "./models_draft.json";
import MODELS_WRITE from "../../../../src/shared/writeModelCatalog.json";
import { ButtonWrapper, Description, ModalStyle, ModelSelectionContainer, OverlayStyle } from "./styles";
import UserStyleExamples from "./UserStyleExamples";
import { BENCHMARK_PICKER_MODEL, BENCHMARK_WRITE_MODEL, BENCHMARK_DRAFT_MODEL } from "../BenchmarkTakeover/proseMode";
import { useBenchmarkTakeoverStore } from "../../stores/benchmarkTakeoverStore";
const dateFormat = require("dateformat");

Modal.setAppElement("#root");

const MaskAnimation = styled.div`
  .maskedText {
    /*
      Use a CSS variable (--reveal) to control where the black→transparent transition begins.
      As --reveal goes from 0%→100%, more text is revealed, with a smooth fade at the trailing edge.
    */
    --reveal: 0%;

    mask-image: linear-gradient(to right, black 0%, black 0%, black calc(var(--reveal) - 5%), transparent var(--reveal), transparent 100%);
    -webkit-mask-image: linear-gradient(to right, black 0%, black 0%, black calc(var(--reveal) - 5%), transparent var(--reveal), transparent 100%);
    mask-type: alpha;
    -webkit-mask-type: alpha;
  }
`;

// Add styled component for the show more link
const ShowMoreLink = styled.button`
  display: inline;
  appearance: none;
  background: none !important;
  border: none;
  font: inherit;
  color: var(--primary-cta-high-contrast) !important;
  cursor: pointer;
  font-weight: 500;
  text-decoration: none;
  padding: 2px 4px !important;
  border-radius: 4px;
  &:hover {
    text-decoration: none;
    background-color: var(--menu-color-active);
  }
  ${focusVisible}
`;

// Add styled component for the learn more link
const LearnMoreLink = styled.a`
  display: inline-flex;
  align-items: center;
  color: var(--primary-cta-high-contrast);
  cursor: pointer !important;
  font-weight: 500;
  text-decoration: none;
  padding: 8px 12px;
  border-radius: 8px;
  margin-right: 12px;
  margin-top: 3px;
  font-size: 14px;
  white-space: nowrap;
  &:hover {
    text-decoration: none;
    background-color: var(--menu-color-active);
  }
  ${focusVisible}
`;

const MyVoiceTrainingButton = styled.button`
  margin-top: 20px !important;
  padding: 8px 12px;
  font-size: 14px;
  display: flex;
  align-self: flex-start;
  width: fit-content;
  align-items: center;
  gap: 6px;
  justify-content: flex-start;
  ${focusVisible}
`;

function addNbspBetweenLastTwoWords(text) {
  const lastSpaceIndex = text.lastIndexOf(" ");
  if (lastSpaceIndex === -1) {
    return text;
  }

  const processedText = text.slice(0, lastSpaceIndex) + "\u00A0" + text.slice(lastSpaceIndex + 1);

  return <div dangerouslySetInnerHTML={{ __html: processedText }} />;
}

function Tag({ label }) {
  return (
    <div className="tag">
      <SVG src="/assets/model-selection-modal/tag.svg" />
      {label}
    </div>
  );
}

function Attribute({ label, className, children }) {
  return (
    <div className={classNames("attribute", className)}>
      <p className="label">{label}</p>
      {children}
    </div>
  );
}

function AttributeText({ label, text }) {
  return (
    <Attribute label={label}>
      <p className="attribute-text">{text}</p>
    </Attribute>
  );
}

function AttributeMoney({ label, value }) {
  return (
    <Attribute label={label}>
      <div className="attribute-money">
        {value === 0 ? (
          <span>Free</span>
        ) : (
          Array.from(Array(5).keys()).map((i) => (
            <span
              key={i}
              className={i < value ? "active" : "inactive"}
            >
              $
            </span>
          ))
        )}
      </div>
    </Attribute>
  );
}

function AttributeNumber({ label, value }) {
  return (
    <Attribute label={label}>
      <div className="attribute-number">
        {Array.from(Array(5).keys()).map((i) => (
          <div
            key={i}
            className={i < value ? "active" : "inactive"}
          />
        ))}
      </div>
    </Attribute>
  );
}

function ModelListMain({ children }) {
  return <ul role="listbox" className="model-list model-list-main">{children}</ul>;
}

function ModelListExpandable({ isExpanded, children }) {
  return <motion.ul className={classNames("model-list model-list-expandable", { expanded: isExpanded })}>{children}</motion.ul>;
}

function ModelListItemMain({ icon, iconFill, label, subLabel, isSelected, onSelect, animatedText, onSubLabelAnimationComplete, modelId, isFree }) {
  const [hasAnimatedSublabel, setHasAnimatedSublabel] = useState(false);

  const playVisualAnimation = isSelected && animatedText && !hasAnimatedSublabel;

  useEffect(() => {
    if (isSelected && animatedText && hasAnimatedSublabel && !playVisualAnimation) {
      // Item is selected, is animated type, has animated before, and is NOT going to play visual animation now.
      // Trigger completion for description visibility.
      if (onSubLabelAnimationComplete) {
        onSubLabelAnimationComplete();
      }
    }
  }, [isSelected, animatedText, hasAnimatedSublabel, playVisualAnimation, onSubLabelAnimationComplete, modelId]);

  return (
    <li
      tabIndex={0}
      role="option"
      aria-selected={isSelected}
      className={classNames("model-list-item-main", { active: isSelected })}
      onClick={onSelect}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(); } }}
    >
      <div className="icon">
        <SVG src={icon} />
      </div>
      <div className="labels">
        <p className="label">{label}{isFree && <span className="new">FREE</span>}</p>
        {animatedText ? (
          <motion.p
            key={modelId + "-sublabel"} // Stable key based on modelId
            className="sublabel"
            initial={{ clipPath: "inset(0 100% 0 0)" }}
            animate={{ clipPath: "inset(0 0% 0 0)" }}
            transition={
              playVisualAnimation
                ? {
                    duration: 1.8,
                    ease: "easeOut",
                    delay: 1.3, // Retaining the 1.3s delay
                  }
                : { duration: 0, delay: 0 }
            }
            onAnimationComplete={() => {
              if (playVisualAnimation) {
                // Only if it *just* visually animated
                setHasAnimatedSublabel(true);
                if (onSubLabelAnimationComplete) {
                  onSubLabelAnimationComplete();
                }
              }
            }}
          >
            {subLabel}
          </motion.p>
        ) : (
          <p className="sublabel">{subLabel}</p>
        )}
      </div>
    </li>
  );
}

function ModelListItemMainExpandable({ icon, iconFill, label, subLabel, isExpanded, onClick }) {
  return (
    <li
      tabIndex={0}
      role="option"
      aria-expanded={isExpanded}
      className={classNames("model-list-item-main", { expanded: isExpanded })}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
    >
      <div className="icon">
        <SVG src={icon} />
      </div>
      <div className="labels">
        <p className="label">{label}</p>
        <p className="sublabel">{subLabel}</p>
      </div>
      <div className="expand-button">
        <SVG src="/assets/down-chevron.svg" />
      </div>
    </li>
  );
}

function ModelListItemSimple({ label, isSelected, onSelect, isNew, isFree }) {
  return (
    <li
      tabIndex={0}
      role="option"
      aria-selected={isSelected}
      className={classNames("model-list-item-simple", { active: isSelected })}
      onClick={onSelect}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(); } }}
    >
      <span className="label">{label}</span>
      {isNew && <span className="new">NEW</span>}
      {isFree && <span className="new">FREE</span>}
    </li>
  );
}

function ModelListItemCategory({ label }) {
  return (
    <li className="model-list-item-category">
      {label}
    </li>
  );
}

function useAvailableModels(writeMode, project) {
  let MODELS_AVAILABLE;
  if (project.useScenes) {
    if (writeMode) {
      MODELS_AVAILABLE = MODELS_WRITE;
    } else {
      MODELS_AVAILABLE = MODELS_DRAFT;
    }
  } else {
    MODELS_AVAILABLE = MODELS_WRITE;
  }
  return useMemo(() => {
    if (writeMode) {
      return MODELS_AVAILABLE.models
        .filter((model) => model.writeModelId !== undefined)
        .map((model) => ({
          ...model,
          modelId: model.writeModelId,
        }));
    } else {
      return MODELS_AVAILABLE.models
        .filter((model) => model.proseModelId !== undefined)
        .map((model) => ({
          ...model,
          modelId: model.proseModelId,
        }));
    }
  }, [writeMode, project]);
}

function useMyVoiceLegacyModels(writeMode, hasAccessToMyVoice) {
  const customModels = useSudowriteStore((state) => state.customModels);

  return useMemo(() => {
    if (writeMode || !hasAccessToMyVoice) {
      return [];
    }

    const customModelsAvailable = customModels.filter(isSelectableLegacyVoiceModel);

    return customModelsAvailable
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .map((model, index) => ({
        ...model,
        type: "simple",
        label: `My Voice ${customModelsAvailable.length - index}`,
        description: `Custom model trained on ${dateFormat(model.createdAt, "mmmm dS, yyyy")}.`,
        modelId: `CUSTOM_${model.id}`,
        tags: [],
        isMyVoice: true,
        myVoiceId: model.id,
        version: model.version ?? 1,
      }));
  }, [writeMode, hasAccessToMyVoice, customModels]);
}

function ModelSubList({ isExpanded, models, selectedModelId, setSelectedModel }) {
  return (
    <ModelListExpandable isExpanded={isExpanded}>
      {models.map((model, i) => {
        switch (model.type) {
          case "category":
            return (
              <ModelListItemCategory
                key={`model_${i}`}
                label={model.label}
              />
            );
          case "simple":
            return (
              <ModelListItemSimple
                key={`model_${i}`}
                label={model.label}
                isSelected={selectedModelId && model.modelId === selectedModelId}
                onSelect={() => setSelectedModel(model)}
                isNew={model.new}
                isFree={model.free}
              />
            );
          default:
            return null;
        }
      })}
    </ModelListExpandable>
  );
}

function getSelectedModel(models, myVoiceModels, selectedModelId) {
  const model = models.find((model) => model.modelId === selectedModelId);

  if (model) {
    return model;
  }

  const myVoiceModel = myVoiceModels.find((model) => `CUSTOM_${model.id}` === selectedModelId);

  if (myVoiceModel) {
    return myVoiceModel;
  }

  return null;
}

// Add a component to handle animated text spans
function AnimatedDescription({ description, showAnimation, animationKey }) {
  const processDescription = (htmlString) => {
    // Parse the HTML string to find animated-text spans
    const parts = htmlString.split(/(<div class='animated-text'>.*?<\/div>)/g);

    return parts.map((part, index) => {
      if (part.includes("class='animated-text'")) {
        // Extract the text content from the span
        const textMatch = part.match(/<div class='animated-text'>(.*?)<\/div>/);
        const textContent = textMatch ? textMatch[1] : "";

        return (
          <MaskAnimation>
            <motion.div
              key={`animated-text-${animationKey}-${index}`}
              className="maskedText"
              initial={{ "--reveal": "0%" }}
              animate={{ "--reveal": showAnimation ? "100%" : "0%" }}
              transition={{
                duration: showAnimation ? 3 : 0,
                ease: "easeInOut",
                delay: showAnimation ? 0 : 0,
              }}
            >
              <span dangerouslySetInnerHTML={{ __html: textContent }} />
            </motion.div>
          </MaskAnimation>
        );
      } else {
        // Regular text or HTML, render as-is
        return (
          <span
            key={index}
            dangerouslySetInnerHTML={{ __html: part }}
          />
        );
      }
    });
  };

  return <>{processDescription(description)}</>;
}

function ModelSelectionModalContent({ isOpen, onClose, selectedModelId, onSelectModelId, writeMode, supportsBenchmarkMode }) {
  const [showHelp, setShowHelp] = useState(false);
  const [internalSelectedModelId, setInternalSelectedModelId] = useState(selectedModelId);
  const [showFullSample, setShowFullSample] = useState(false);
  const [showDescriptionAnimation, setShowDescriptionAnimation] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const [selectionError, setSelectionError] = useState("");
  const selectionRequestRef = useRef(0);
  useEffect(() => () => { selectionRequestRef.current += 1; }, []);
  useEffect(() => { if (!isOpen) selectionRequestRef.current += 1; }, [isOpen]);
  const hasAccessToMuse2AlphaPhase2 = useSudowriteStore((state) => !!state.subscription?.hasAccessToMuse2AlphaPhase2);
  const hasAccessToMyVoice = useSudowriteStore((state) => state.subscription)?.hasAccessToMyVoice;
  const hasAccessToMyVoiceWrite = useSudowriteStore((state) => state.subscription)?.hasAccessToMyVoiceWrite;
  const project = useProjectStore((state) => state.project);
  const hasAccessToMuseBeta = useSudowriteStore((state) => state.subscription)?.hasAccessToMuseBeta;
  const availableModels = useAvailableModels(writeMode, project);
  const isInUnlimitedExperiment = useSudowriteStore((state) => state.isInUnlimitedExperiment);
  const sprint = useNovelNovemberSprint();
  // Novel November funds only Excellent; the server still admits each request.
  const sprintFreeModelId = writeMode ? sprint.writeAvailable && "excellent" : sprint.draftAvailable && "EXCELLENT";
  const { styleSamples, isLoading: mvSamplesLoading, mutateSamples } = useMyVoiceStyleSamples();
  const { selectedStyleSampleId, setSelectedSample, isLoading: mvSelectionLoading, mutateSelected } = useMyVoiceSelectedSample();
  const history = useHistory();

  // Filter out MUSE models if user doesn't have access to Muse beta
  const models = useMemo(() => {
    let models = availableModels;
    // Phase 1 standalone turtles were removed when Phase 2 launched; the
    // catalog no longer carries them. The Phase 2 comparison entry is added
    // separately below.
    const benchmarkModelId = writeMode ? BENCHMARK_WRITE_MODEL : BENCHMARK_DRAFT_MODEL;
    if (supportsBenchmarkMode && hasAccessToMuse2AlphaPhase2) {
      const benchmarkModel = {
        ...BENCHMARK_PICKER_MODEL,
        modelId: benchmarkModelId,
        userStyleExamples: !writeMode,
      };
      const basicIndex = models.findIndex((model) => model.writeModelId === "basic");
      const insertAt = basicIndex >= 0 ? basicIndex + 1 : models.findIndex((model) => model.type === "main-expandable");
      const insertionIndex = insertAt >= 0 ? insertAt : models.length;
      models = [...models.slice(0, insertionIndex), benchmarkModel, ...models.slice(insertionIndex)];
    }
    if (!hasAccessToMuseBeta) {
      models = models.filter((model) => {
        // Filter out MUSE03, MUSE04, MUSE05, and MUSE06 models
        return !(model.proseModelId === "MUSE03" || model.proseModelId === "MUSE04" || model.proseModelId === "MUSE05" || model.proseModelId === "MUSE06");
      });
    }

    // Temp Unlimited Sudowrite Experiment
    if (isInUnlimitedExperiment) {
      models = models.map((model) => {
        return model.label === "Basic"
          ? {
              ...model,
              label: "Unlimited",
              subLabel: "Good quality & free",
              description: `Fast and economical, with good prose. You may notice more clichés or logical mistakes compared to Muse or Excellent. <p style='color: var(--primary-cta-high-contrast); margin-top: 10px;'> Free for a limited time.</p>`,
              creditsUsed: 0,
            }
          : model;
      });
    }

    // Gate the My Voice write option until user trains a model
    if (writeMode && styleSamples.length === 0) {
      models = models.filter((model) => model.modelId !== "myVoice");
    } else if (!writeMode && styleSamples.length === 0) {
      models = models.filter((model) => model.modelId !== "MY_VOICE");
    }

    // Sort only Experimental presentation rows; preserve catalog order for other consumers.
    const experimentalStart = models.findIndex((model) => model.type === "category" && model.label === "Experimental");
    if (experimentalStart >= 0) {
      const positions = [];
      for (let i = experimentalStart + 1; i < models.length && models[i].type !== "category"; i++) {
        if (models[i].type === "simple") positions.push(i);
      }
      const sorted = positions.map((index) => models[index]).sort((a, b) => a.label.localeCompare(b.label));
      models = [...models];
      positions.forEach((position, index) => {
        models[position] = sorted[index];
      });
    }
    return models;
  }, [availableModels, supportsBenchmarkMode, hasAccessToMuse2AlphaPhase2, selectedModelId, hasAccessToMuseBeta, isInUnlimitedExperiment, writeMode, hasAccessToMyVoiceWrite, hasAccessToMyVoice, styleSamples]);

  const myVoiceLegacyModels = useMyVoiceLegacyModels(writeMode, hasAccessToMyVoice);
  const selectedModel = getSelectedModel(models, myVoiceLegacyModels, internalSelectedModelId);
  const myVoiceTopModel = useMemo(() => models.find((m) => m.modelId === "myVoice" || m.modelId === "MY_VOICE"), [models]);
  const storyBibleScene = useStoryBibleStore((state) => state.scenes[state.sceneId]);
  const setScenes = useStoryBibleStore((state) => state.setScenes);
  const setDraftLastUsedCreativity = useSudowriteStore((state) => state.setDraftLastUsedCreativity);
  const originalSelectedModelRef = useRef(null);
  const currentSelectedModelRef = useRef(null);

  // These two useEffects work together to track the original and current selected models and send an event when the model changes.
  useEffect(() => {
    currentSelectedModelRef.current = selectedModel;
    if (originalSelectedModelRef.current === null && selectedModel) {
      originalSelectedModelRef.current = selectedModel;
    }
  }, [selectedModel]);

  useEffect(() => {
    // This only runs on component unmount, when the modal is closed.
    return () => {
      if (originalSelectedModelRef.current !== null && currentSelectedModelRef.current !== null) {
        const hasChanged = originalSelectedModelRef.current?.modelId !== currentSelectedModelRef.current?.modelId;

        if (hasChanged) {
          sendAppEvent("Model Selection Modal - Model Changed", {
            fromModelLabel: originalSelectedModelRef.current?.label,
            toModelLabel: currentSelectedModelRef.current?.label,
            fromModelId: originalSelectedModelRef.current?.modelId,
            toModelId: currentSelectedModelRef.current?.modelId,
            modelSelector: writeMode ? "Write" : "Draft",
          });
        }
      }
    };
  }, []);

  // When the modal opens, refresh My Voice data and sync internal selection with prop
  useEffect(() => {
    if (!isOpen) return;
    try {
      mutateSamples && mutateSamples();
      mutateSelected && mutateSelected();
    } catch (_) {}
    setInternalSelectedModelId(selectedModelId);
  }, [isOpen, selectedModelId, mutateSamples, mutateSelected]);

  // Reset animations when selected model changes
  useEffect(() => {
    setShowFullSample(false);
    // Only reset description animation if the model doesn't have animatedText
    // If it does have animatedText, the setSelectedModel function will handle the animation logic
    const currentSelectedModel = getSelectedModel(models, myVoiceLegacyModels, internalSelectedModelId);
    if (!currentSelectedModel?.animatedText) {
      setShowDescriptionAnimation(false);
    }
  }, [internalSelectedModelId, models, myVoiceLegacyModels]);

  const setSelectedModel = async (model) => {
    if (model.disabled) {
      return;
    }
    const selectionRequest = ++selectionRequestRef.current;
    setSelectionError("");
    if (model.writeModelId === BENCHMARK_WRITE_MODEL) {
      try {
        await useBenchmarkTakeoverStore.getState().enableBenchmarkMode();
        // A slow consent response must not overwrite a newer selection or save
        // a mode after the picker has been dismissed.
        if (selectionRequest !== selectionRequestRef.current) return;
      } catch (error) {
        if (selectionRequest === selectionRequestRef.current) {
          setSelectionError(error?.message || "Couldn't enable Muse 2 Alpha. Please try again.");
        }
        return;
      }
    }
    const previouslySelectedModel = getSelectedModel(models, myVoiceLegacyModels, internalSelectedModelId);
    const isSameModelClicked = internalSelectedModelId === model.modelId;

    if (isSameModelClicked) {
      if (model.animatedText && model.type === "simple" && !showDescriptionAnimation) {
        setTimeout(() => {
          setShowDescriptionAnimation(true);
        }, 50); // Show if simple, animated, and not already shown
      }
      // animationKey is not changed for same model click.
      // The sublabel in ModelListItemMain also won't re-animate due to its internal hasAnimatedSublabel state.
      if (!model.disabled) onSelectModelId(model.modelId); // Parent might still want to know about the re-click
      return;
    }

    // Switching to a new/different model
    setInternalSelectedModelId(model.modelId); // Update to new model ID first

    const newModelIsAnimated = model.animatedText;
    const previousModelWasAnimated = previouslySelectedModel?.animatedText;

    if (newModelIsAnimated) {
      const descriptionTextHasChanged = model.description !== previouslySelectedModel?.description;

      // Update animationKey for description *content* only if coming from non-animated or if description text actually changed.
      if (!previousModelWasAnimated || descriptionTextHasChanged) {
        setAnimationKey((prev) => prev + 1);
      }
      // If previousModelWasAnimated and descriptionText is the same, animationKey does NOT change.

      if (!previousModelWasAnimated) {
        // Transition: Non-Animated (or None) -> Animated Model
        // We want a fresh reveal of the description area.
        setShowDescriptionAnimation(false); // Hide area briefly.
      }
      // For 'main' types, wait for the sublabel clipPath animation (1.3s delay + 1.8s duration) before revealing the description.
      // The onSubLabelAnimationComplete callback from ModelListItemMain may also fire, but it is unreliable when the sublabel's animate target does not change (e.g. switching into an already-mounted main item).
      const descRevealDelay = model.type === "simple" ? 50 : 3150;
      setTimeout(() => setShowDescriptionAnimation(true), descRevealDelay);
    } else {
      // Transition: Any Model -> Non-Animated Model
      setShowDescriptionAnimation(false); // Hide description area.
    }

    if (!model.disabled) {
      onSelectModelId(model.modelId);
    }
  };

  const [expandedContainer, setExpandedContainer] = useState(() => {
    if (selectedModel && selectedModel.type === "simple") {
      return selectedModel.isMyVoice ? "myVoice" : "advanced";
    } else {
      return null;
    }
  });

  // Helper function to truncate sample to specified word count while preserving paragraphs
  const truncateSample = (sample, wordLimit) => {
    if (!sample) return "";

    const paragraphs = sample.split("\n");
    let wordCount = 0;
    let truncatedParagraphs = [];

    // Process each paragraph until we hit the word limit
    for (let i = 0; i < paragraphs.length; i++) {
      const paragraphWords = paragraphs[i].split(/\s+/);

      // If adding this paragraph would exceed the limit
      if (wordCount + paragraphWords.length > wordLimit) {
        // How many more words we can add
        const wordsLeftToAdd = wordLimit - wordCount;

        if (wordsLeftToAdd > 0) {
          // Add a partial paragraph
          truncatedParagraphs.push(paragraphWords.slice(0, wordsLeftToAdd).join(" ") + "...");
        }
        break;
      }

      // Add the whole paragraph
      truncatedParagraphs.push(paragraphs[i]);
      wordCount += paragraphWords.length;

      // If we've hit the limit exactly, stop
      if (wordCount >= wordLimit) break;
    }

    return truncatedParagraphs;
  };

  const handleSubLabelAnimationComplete = () => {
    setShowDescriptionAnimation(true);
  };

  const handleUseModelClick = () => {
    try {
      if (
        (selectedModel?.writeModelId === "myVoice" || selectedModel?.proseModelId === "MY_VOICE" || selectedModel?.isMyVoice) &&
        hasAccessToMyVoiceWrite &&
        !mvSamplesLoading &&
        styleSamples.length === 0
      ) {
        history.push("/app/my-voice");
      }
    } finally {
      onClose();
    }
  };

  return (
    <>
      <div className="left-panel">
        <div className="header">
          <h2>Prose Mode</h2>
          {selectionError && <p role="alert">{selectionError}</p>}
          <button
            type="button"
            className="help-button"
            onMouseEnter={() => setShowHelp(true)}
            onMouseLeave={() => setShowHelp(false)}
            onFocus={() => setShowHelp(true)}
            onBlur={() => setShowHelp(false)}
            aria-label="Help"
          >
            ?
          </button>
        </div>

        <ModelListMain>
          {models.map((model, i) => {
            if (model.type === "main") {
              return (
                <ModelListItemMain
                  key={`model_${i}`}
                  modelId={model.modelId}
                  label={model.label}
                  icon={model.icon}
                  iconFill={model.iconFill}
                  subLabel={model.subLabel}
                  isSelected={internalSelectedModelId && model.modelId === internalSelectedModelId}
                  onSelect={() => setSelectedModel(model)}
                  animatedText={model.animatedText}
                  onSubLabelAnimationComplete={handleSubLabelAnimationComplete}
                  isFree={model.modelId === sprintFreeModelId}
                />
              );
            } else if (model.type === "main-expandable" && model.section === "advanced") {
              return (
                <React.Fragment key={`model_${i}`}>
                  <ModelListItemMainExpandable
                    label={model.label}
                    icon={model.icon}
                    iconFill={model.iconFill}
                    subLabel={model.subLabel}
                    onClick={() => setExpandedContainer((expandedContainer) => (expandedContainer === model.section ? null : model.section))}
                    isExpanded={expandedContainer === model.section}
                  />
                  <ModelSubList
                    isExpanded={expandedContainer === model.section}
                    models={models}
                    selectedModelId={internalSelectedModelId}
                    setSelectedModel={setSelectedModel}
                  />
                </React.Fragment>
              );
            } else if (model.type === "main-expandable" && model.section === "myVoice" && hasAccessToMyVoice) {
              return (
                <React.Fragment key={`model_${i}`}>
                  <ModelListItemMainExpandable
                    label={model.label}
                    icon={model.icon}
                    iconFill={model.iconFill}
                    subLabel={model.subLabel}
                    onClick={() => setExpandedContainer((expandedContainer) => (expandedContainer === model.section ? null : model.section))}
                    isExpanded={expandedContainer === model.section}
                  />
                  <ModelSubList
                    isExpanded={expandedContainer === model.section}
                    models={myVoiceLegacyModels}
                    selectedModelId={internalSelectedModelId}
                    setSelectedModel={setSelectedModel}
                  />
                </React.Fragment>
              );
            } else {
              return null;
            }
          })}
        </ModelListMain>
      </div>

      {selectedModel && (
        <div
          className="model"
          data-has-headline={!!selectedModel.headlineImage}
        >
          <div className="description">
            <IconButton
              className="icon-button-close"
              onClick={onClose}
              src="/assets/close.svg"
              ariaLabel="Close model selection modal"
              hoverTipText="Close"
              iconSize={32}
              backgroundOverride={!!selectedModel.headlineImage}
              disableTooltip={true}
            />
            <ModelSelectionContainer
              hasHeadline={!!selectedModel.headlineImage}
              customBackground={!!selectedModel.headlineImage}
            >
              <Description
                hasHeadline={!!selectedModel.headlineImage}
                customBackground={!!selectedModel.headlineImage}
              >
                {selectedModel.headlineImage && (
                  <div className="headline">
                    <InlineSVG
                      src={selectedModel.headlineImage}
                      width={500}
                      height={90}
                    />
                  </div>
                )}
                {selectedModel.animatedText ? (
                  <div className="description-text">
                    <AnimatedDescription
                      description={selectedModel.description ?? ""}
                      showAnimation={showDescriptionAnimation}
                      animationKey={animationKey}
                    />
                  </div>
                ) : (
                  <div className="description-text">{addNbspBetweenLastTwoWords(selectedModel.description ?? "")}</div>
                )}
              </Description>

              {selectedModel.contentLayout === "headline" && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "12px" }}>
                  {selectedModel.learnMorelink && (
                    <LearnMoreLink
                      href={selectedModel.learnMorelink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Learn More
                    </LearnMoreLink>
                  )}
                  <ButtonWrapper
                    align="center"
                    modelId={selectedModel.writeModelId}
                  >
                    <div className="muse-button-container">
                      {selectedModel.writeModelId === "muse" && (
                        <div className="muse-mask">
                          <div className="muse-glow-outer"></div>
                          <div className="muse-glow-inner"></div>
                        </div>
                      )}
                      <button
                        onClick={handleUseModelClick}
                        disabled={selectedModel.disabled}
                      >
                        Use {selectedModel.label ?? "Model"}
                      </button>
                    </div>
                  </ButtonWrapper>
                </div>
              )}
            </ModelSelectionContainer>
          </div>
          <div className="attributes">
            {selectedModel.proseQuality !== undefined && (
              <AttributeNumber
                label="Prose Quality"
                value={selectedModel.proseQuality}
              />
            )}
            {selectedModel.instructionFollowing !== undefined && (
              <AttributeNumber
                label="Instruction following"
                value={selectedModel.instructionFollowing}
              />
            )}
            {selectedModel.unfiltered !== undefined && (
              <AttributeNumber
                label="Unfiltered"
                value={selectedModel.unfiltered}
              />
            )}
            {selectedModel.coreModel !== undefined && (
              <AttributeText
                label="Base Model"
                text={selectedModel.coreModel}
              />
            )}
            {selectedModel.version !== undefined && (
              <AttributeText
                label="Version"
                text={selectedModel.version ?? 1}
              />
            )}
            {selectedModel.myVoiceId !== undefined && (
              <AttributeText
                label="ID"
                text={capitalize(selectedModel.myVoiceId)}
              />
            )}
            {selectedModel.originalFilename !== undefined && (
              <AttributeText
                label="Filename"
                text={capitalize(selectedModel.originalFilename)}
              />
            )}
            {selectedModel.creditsUsed !== undefined && (
              <AttributeMoney
                label="Credits use"
                value={selectedModel.creditsUsed}
              />
            )}
            {selectedModel.baseModel !== undefined && (
              <AttributeText
                label="Base Model"
                text={selectedModel.baseModel}
              />
            )}
            {selectedModel.tags?.map((tag) => (
              <Tag
                key={tag}
                label={tag}
              />
            ))}
          </div>

          {(selectedModel?.writeModelId === "myVoice" || selectedModel?.proseModelId === "MY_VOICE" || selectedModel?.isMyVoice) && styleSamples.length > 0 && (
            <div style={{ marginTop: "16px", padding: "8px 0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <InlineSVG
                  src="/assets/my-voice.svg"
                  width={20}
                  height={20}
                />
                <p style={{ margin: 0, fontFamily: "Inter", fontSize: "14px", color: "var(--grey-0)" }}>Select a Voice</p>
              </div>
              <MenuSelect
                label="My Voice style sample"
                placeholder={mvSamplesLoading ? "Loading..." : styleSamples.length ? "Choose a style" : "No style samples yet"}
                menuType="radiobutton"
                options={styleSamples.map((s) => ({ value: s.sample_id, label: s.name }))}
                value={selectedStyleSampleId || (styleSamples.length > 0 ? styleSamples[0].sample_id : undefined)}
                optionAlignment="left"
                disabled={mvSamplesLoading || mvSelectionLoading || styleSamples.length === 0}
                portalContainerSelector=".ReactModal__Content"
                collisionBoundarySelector=".ReactModal__Content"
                onChange={async (value) => {
                  try {
                    await setSelectedSample(value);
                    if (myVoiceTopModel && internalSelectedModelId !== myVoiceTopModel.modelId) {
                      setSelectedModel(myVoiceTopModel);
                    }
                  } catch (error) {
                    captureException(error);
                  }
                }}
              />
              <MyVoiceTrainingButton
                type="button"
                onClick={() => {
                  history.push("/app/my-voice");
                  onClose();
                }}
              >
                Train a New Voice
              </MyVoiceTrainingButton>
            </div>
          )}

          {selectedModel.creativitySelector && (
            <div className="creativity-slider">
              <div className="creativity-text">
                <h3>Creativity</h3>
                <p className="creativity-subhead">Allows more creative wording, but limits instruction following.</p>
              </div>
              <Slider
                values={[
                  { label: "1 - Most Reliable", value: 10 },
                  { label: "2", value: 20 },
                  { label: "3", value: 30 },
                  { label: "4", value: 40 },
                  { label: "5", value: 50 },
                  { label: "6", value: 60 },
                  { label: "7 - Recommended", value: 70 },
                  { label: "8", value: 80 },
                  { label: "9", value: 90 },
                  { label: "10 - Very Creative", value: 95 },
                  {
                    label: "11 - Maybe too Creative",
                    value: 100,
                  },
                ]}
                currentValue={storyBibleScene?.creativityLevel || 70}
                onChange={(creativityLevel) => {
                  setScenes((draft) => {
                    draft[storyBibleScene.id].creativityLevel = creativityLevel;
                  });
                  setDraftLastUsedCreativity(project?.id, creativityLevel);
                }}
              />
            </div>
          )}

          {/* Keyed by silo: switching between an alpha candidate and a regular
              model must remount so local state re-derives from the right field —
              a reused instance would debounce-write one silo's text into the
              other. */}
          {selectedModel.userStyleExamples && (
            <UserStyleExamples
              key={selectedModel.muse2Alpha ? "muse2-alpha" : "standard"}
              muse2Alpha={!!selectedModel.muse2Alpha}
            />
          )}

          {selectedModel.greatFor && (
            <div className="great-for">
              <strong>Great for:</strong> {selectedModel.greatFor}
            </div>
          )}

          {selectedModel.sample !== undefined && (
            <div className="writing-sample">
              <header>Writing Sample</header>
              <p className="writing-sample-subhead">What you can expect from this mode</p>
              <div className="samples">
                {!showFullSample ? (
                  <>
                    {truncateSample(selectedModel.sample, 100).map((text, i) => (
                      <p key={i}>
                        {text}
                        {i === truncateSample(selectedModel.sample, 100).length - 1 && (
                          <>
                            {" "}
                            <ShowMoreLink onClick={() => setShowFullSample(true)}>Show more</ShowMoreLink>
                          </>
                        )}
                      </p>
                    ))}
                  </>
                ) : (
                  <>
                    {selectedModel.sample.split("\n").map((text, i) => (
                      <p key={i}>{text}</p>
                    ))}
                  </>
                )}
              </div>
            </div>
          )}

          {selectedModel.contentLayout !== "headline" && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "12px" }}>
              {selectedModel.learnMorelink && (
                <LearnMoreLink
                  href={selectedModel.learnMorelink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learn More
                </LearnMoreLink>
              )}
              <ButtonWrapper
                align="right"
                modelId={selectedModel.writeModelId}
              >
                <button
                  onClick={handleUseModelClick}
                  disabled={selectedModel.disabled}
                >
                  Use {selectedModel.label ?? "Model"}
                </button>
              </ButtonWrapper>
            </div>
          )}
        </div>
      )}

      <AnimatePresence>
        {showHelp && (
          <motion.div
            className="help"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            {MODELS_DRAFT.help.split("\n").map((text, i) => (
              <p key={i}>{text}</p>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function ModelSelectionModal({
  isOpen,
  onClose,
  selectedModelId,
  onSelectModelId,
  writeMode,
  supportsBenchmarkMode = false,
  overlayZIndex,
}) {
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      closeTimeoutMS={300}
      shouldCloseOnOverlayClick={true}
      className="_"
      overlayClassName="_"
      contentElement={(props, children) => <ModalStyle {...props}>{children}</ModalStyle>}
      overlayElement={(props, contentElement) => (
        <OverlayStyle
          {...props}
          $zIndex={overlayZIndex}
          $pointerEvents={overlayZIndex != null ? "auto" : undefined}
        >
          {contentElement}
        </OverlayStyle>
      )}

    >
      <ModelSelectionModalContent
        isOpen={isOpen}
        onClose={onClose}
        selectedModelId={selectedModelId}
        onSelectModelId={onSelectModelId}
        writeMode={writeMode}
        supportsBenchmarkMode={supportsBenchmarkMode}
      />
    </Modal>
  );
}

export default ModelSelectionModal;
